import { type RefObject, useEffect, useRef, useState } from 'react'

type RevealOptions = { threshold?: number; rootMargin?: string }

/**
 * `visible` turns true the first time the ref'd element enters the viewport, and stays true: the
 * observer disconnects on reveal and on unmount. Ports the three observers of `app.js:843-888`,
 * `1168-1203` and `1267-1303`, which differ only in what they watch and their threshold/rootMargin.
 *
 * It only reports. Whatever hides the element until then must be CSS gated on BOTH
 * `prefers-reduced-motion: no-preference` and `scripting: enabled` (see `<Reveal>`), so content never
 * waits on this hook for someone who turned animation off or runs without JS.
 */
export default function useRevealOnView<T extends Element>({ threshold = 0, rootMargin = '0px' }: RevealOptions = {}): {
  ref: RefObject<T | null>
  visible: boolean
} {
  const ref = useRef<T>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const element = ref.current
    // KaiOS 2.5 is the one `defaults` browser without IntersectionObserver. Returning is enough: it
    // predates the `scripting` media query too, so `<Reveal>`'s hidden state never applies there.
    if (!element || !('IntersectionObserver' in window)) return undefined

    // eslint-disable-next-line compat/compat -- guarded above
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setVisible(true)
        observer.disconnect()
      },
      { threshold, rootMargin },
    )
    observer.observe(element)

    return (): void => observer.disconnect()
  }, [threshold, rootMargin])

  return { ref, visible }
}
